import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack } from "@mui/system";
import { Button, LinearProgress } from "@mui/material";
import AdminProcessReceiptModal from "./AdminProcessReceiptModal";
import { setNotification } from "../../../redux/slices/notificationsSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";

const ReceiptsTable = ({
  data,
  processedTableData,
  onPageChange,
  onChangeRowsPerPage,
  onProcessedDataPageChange,
  onProcessedDataRowChange,
  onSuccessUpdateReceipt
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const getAllUnprocessedReceiptsLoading = useSelector(
    (state) => state.admin.getAllUnprocessedReceiptsLoading
  );
  const getAllProcessedReceiptsLoading = useSelector(
    (state) => state.admin.getAllProcessedReceiptsLoading
  );
  const unProcessedReceiptsCount = useSelector(
    (state) => state.admin.unProcessedReceiptsCount
  );
  const processedReceiptsCount = useSelector(
    (state) => state.admin.processedReceiptsCount
  );
  const [loading, setLoading] = useState(false);
  const [showProcessReceiptModal, setShowProcessReceiptModal] = useState(false);
  const [receiptToProcess, setReceiptToProcess] = useState();
  const columns = [
    { id: "valid", label: t("receiptsProcessing.valid"), minWidth: 50 },
    { id: "objectId", label: t("receiptsProcessing.id"), minWidth: 170 },
    { id: "username", label: t("receiptsProcessing.username"), minWidth: 170 },
    {
      id: "createdAt",
      label: t("receiptsProcessing.createdAt"),
      minWidth: 100,
    },
    {
      id: "picture",
      label: t("receiptsProcessing.picture"),
      minWidth: 170,
    },
    {
      id: "action",
      label: t("adminGifts.actions"),
    },
  ];

  useEffect(() => {
    setLoading(
      getAllProcessedReceiptsLoading === "PENDING" ||
        getAllUnprocessedReceiptsLoading === "PENDING"
    );
  }, [data, getAllProcessedReceiptsLoading, getAllUnprocessedReceiptsLoading]);

  const handleChangePage = (event, newPage) => {
    if (processedTableData) {
      onProcessedDataPageChange(newPage);
    } else {
      onPageChange(newPage);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (processedTableData) {
      onProcessedDataRowChange({ page: 0, rowPage: +event.target.value });
    } else {
      onChangeRowsPerPage(+event.target.value);
    }
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const onProcessAction = (value) => {
    setReceiptToProcess(value);
    setShowProcessReceiptModal(true);
  };
  const onCloseReceiptToProcessModal = () => {
    setShowProcessReceiptModal(false);
    setReceiptToProcess(null);
  };
  const handleOnSuccessUpdateReceipt = (pointsCustomerGot) => {
    onSuccessUpdateReceipt();
    dispatch(
      setNotification({
        show: true,
        message: t("notifications.successUpdatedReceipt"),
        variant: "success",
      })
    );
    setShowProcessReceiptModal(false);
  };
  const getIconForVerify = (value) => {
    return value ? <CheckCircleIcon /> : <DoDisturbIcon />;
  };
  const getColumn = (value, column) => {
    const isActionColumn = column.id === "action";
    let content = null;

    if (isActionColumn) {
      content = (
        <TableCell key={column.id} align={column.align}>
          <Stack direction="column">
            <Button onClick={() => onProcessAction(value)} variant="contained">
              {t(
                processedTableData
                  ? "receiptsProcessing.preview"
                  : "receiptsProcessing.process"
              )}
            </Button>
          </Stack>
        </TableCell>
      );
    } else if (column.id === "picture") {
      content = (
        <TableCell key={column.id} align={column.align}>
          <img src={value?.url} alt="prize-img" height="150px" width="150px" />
        </TableCell>
      );
    } else {
      content = (
        <TableCell key={column.id} align={column.align}>
          {column.id === "valid" ? getIconForVerify(value) : value}
        </TableCell>
      );
    }

    return (
      <TableCell key={column.id} align={column.align}>
        {content}
      </TableCell>
    );
  };
  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          ) : (
            <TableBody>
              {data.map((receipt) => {
                return (
                  <TableRow
                    role="checkbox"
                    tabIndex={-1}
                    key={receipt.objectId}
                    sx={{
                      backgroundColor: receipt.valid ? "white" : "#ff000052",
                    }}
                  >
                    {columns.map((column) => {
                      const value =
                        column.id === "action" ? receipt : receipt[column.id];
                      return getColumn(value, column);
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={
          processedTableData ? processedReceiptsCount : unProcessedReceiptsCount
        }
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {showProcessReceiptModal && (
        <AdminProcessReceiptModal
          open={showProcessReceiptModal}
          receiptToProcess={receiptToProcess}
          onClose={onCloseReceiptToProcessModal}
          onSuccessUpdateReceipt={handleOnSuccessUpdateReceipt}
          disableActions={processedTableData}
        />
      )}
    </Paper>
  );
};
export default ReceiptsTable;
